import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";

export const BeforeAfter = () => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  const drag = (e) => {
    update(e.clientX ?? e.touches?.[0]?.clientX);
    const move = (ev) => update(ev.clientX ?? ev.touches?.[0]?.clientX);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("touchend", up);
  };

  return (
    <section data-testid="before-after-section" className="bg-brand-navy py-24 lg:py-32">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-fall-amber sm:text-sm">The Oxford difference</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Stained this morning. Spotless by Friday.
          </h2>
          <p className="mt-4 text-base text-slate-300">Drag the handle — this is what we do all day, every day.</p>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div
            ref={ref}
            data-testid="before-after-slider"
            onPointerDown={drag}
            className="relative aspect-[4/3] cursor-ew-resize touch-none select-none overflow-hidden rounded-[2rem] border border-white/15 shadow-2xl shadow-black/50 sm:aspect-[16/9]"
          >
            <img
              src="/images/shirt_clean.webp"
              onError={(e) => (e.currentTarget.src = "/images/shirt_clean.png")}
              alt="Spotless white dress shirt after professional cleaning"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
              draggable={false}
            />
            <div
              className="pointer-events-none absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            >
              <img
                src="/images/shirt_dirty.webp"
                onError={(e) => (e.currentTarget.src = "/images/shirt_dirty.png")}
                alt="Stained white dress shirt before cleaning"
                className="absolute inset-0 h-full w-full object-cover"
                draggable={false}
              />
              <span className="absolute left-4 top-4 rounded-full bg-rose-600/90 px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-white">
                Before
              </span>
            </div>
            <span className="absolute right-4 top-4 rounded-full bg-emerald-500/90 px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-white">
              After
            </span>
            <div
              data-testid="before-after-handle"
              className="absolute inset-y-0 z-10"
              style={{ left: `${pos}%` }}
            >
              <div className="absolute inset-y-0 -ml-px w-0.5 bg-white shadow-[0_0_18px_rgba(255,255,255,0.9)]" />
              <motion.div
                animate={{ scale: [1, 1.12, 1] }}
                transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                className="absolute top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-fall-amber shadow-xl"
              >
                <MoveHorizontal className="h-5 w-5 text-brand-navy" />
              </motion.div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
